import { useQuery } from '@tanstack/react-query';
import { http } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import type { FulfillmentJob } from '@/services';
import { orderAPI } from '@/services/orders';
import type { ListPage } from '@/services/list-envelope';
import { FULFILLMENT_JOB_FILTERS } from '@/services/filters.generated';
import { toApiListParams, type ListRequestParams } from '@/services/list-request';
import { buildSearchRequests, groupTasks, MIN_SEARCH_CHARS } from './searchQueries';

const STALE_MS = 30_000;

const fetchJobs = (p: ListRequestParams) =>
  http.get<ListPage<FulfillmentJob>>('/api/fulfillment-jobs', {
    params: toApiListParams(p, FULFILLMENT_JOB_FILTERS),
  });

/**
 * The three search groups for `term`. A query runs only while the palette is
 * open, the term is long enough and the caller may read that group; each one
 * fails on its own (its `isError`), never taking the others down.
 */
export function useGlobalSearch(
  term: string,
  open: boolean,
  can: { orders: boolean; dispatch: boolean },
) {
  const ready = open && term.length >= MIN_SEARCH_CHARS;
  const req = buildSearchRequests(term);
  const orders = useQuery({
    queryKey: queryKeys.globalSearch.orders(term),
    queryFn: () => orderAPI.getOrders(req.orders),
    enabled: ready && can.orders,
    staleTime: STALE_MS,
  });
  const jobs = useQuery({
    queryKey: queryKeys.globalSearch.jobs(term),
    queryFn: () => fetchJobs(req.jobs),
    enabled: ready && can.dispatch,
    staleTime: STALE_MS,
  });
  const tasks = useQuery({
    queryKey: queryKeys.globalSearch.tasks(term),
    queryFn: async () => groupTasks((await fetchJobs(req.tasks)).items),
    enabled: ready && can.dispatch,
    staleTime: STALE_MS,
  });
  return { orders, jobs, tasks };
}
